import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

const LanguageSelector = ({ darkMode = null }) => {
  const { i18n, t } = useTranslation('common');
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem("languageManuallySet", "true");
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('changeLanguage')}>
        <IconButton
          onClick={handleClick}
          sx={{
            backgroundColor: darkMode === null 
              ? `${colors.blueAccent[700]}30`
              : darkMode 
                ? `${colors.blueAccent[700]}30` 
                : `${colors.blueAccent[100]}40`,
            border: darkMode === null
              ? `1px solid ${colors.blueAccent[500]}40`
              : darkMode 
                ? `1px solid ${colors.blueAccent[500]}40` 
                : `1px solid ${colors.blueAccent[300]}60`,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: darkMode === null
                ? `${colors.blueAccent[600]}50`
                : darkMode 
                  ? `${colors.blueAccent[600]}50` 
                  : `${colors.blueAccent[200]}60`,
              transform: 'scale(1.05)',
              borderColor: darkMode === null
                ? colors.blueAccent[500]
                : darkMode 
                  ? colors.blueAccent[500] 
                  : colors.blueAccent[400],
            },
          }}
        >
          <LanguageIcon sx={{ 
            color: darkMode === null
              ? colors.blueAccent[300]
              : darkMode 
                ? colors.blueAccent[300] 
                : colors.blueAccent[600] 
          }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.blueAccent[500]}40`,
            borderRadius: '12px',
            mt: 1,
            minWidth: 180,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={i18n.language === language.code}
            sx={{
              backgroundColor:
                i18n.language === language.code
                  ? `${colors.blueAccent[700]}30`
                  : 'transparent',
              '&:hover': {
                backgroundColor: `${colors.blueAccent[700]}20`,
              },
              py: 1.5,
              px: 2,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Box
                component="span"
                sx={{
                  fontSize: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {language.flag}
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={language.name}
              primaryTypographyProps={{
                sx: {
                  color: colors.grey[100],
                  fontWeight: i18n.language === language.code ? 600 : 400,
                },
              }}
            />
            {i18n.language === language.code && (
              <CheckIcon
                sx={{
                  color: colors.greenAccent[400],
                  ml: 1,
                  fontSize: '1.2rem',
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
